'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Product {
  id: string;
  name: string;
  description: string;
  image: string;
  alt: string;
  href: string;
}

interface HoursEntry {
  day: string;
  dayFull: string;
  from: string;
  to: string;
}

// ─── Data ────────────────────────────────────────────────────────────────────
const products: Product[] = [
{
  id: 'jollof-rice',
  name: 'Jollof Rice',
  description: 'Experience the authentic taste of Jollof Rice.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ec45c5a5-1772880148244.png",
  alt: 'Jollof Rice - a classic Nigerian rice dish cooked in tomato sauce',
  href: '#products'
},
{
  id: 'efo-riro',
  name: 'Efo Riro',
  description: 'Delight in the nourishing Efo Riro soup.',
  image: "/assets/images/Efo_Riro_2-1784762475437.PNG",
  alt: 'Efo Riro - a rich Nigerian spinach stew with assorted meats',
  href: '#products'
},
{
  id: 'pepper-soup',
  name: 'Pepper Soup',
  description: 'Warm up with flavorful Pepper Soup.',
  image: "/assets/images/Pepper_soup-1784765082888.jpg",
  alt: 'Pepper Soup - a spicy Nigerian broth with meat and aromatic spices',
  href: '#products'
},
{
  id: 'pepper-snails',
  name: 'Pepper Snails',
  description: 'A popular, spicy, and chewy dish made from snails-coated',
  image: "/assets/images/Pepper_Snails-1784765181098.jpg",
  alt: 'Pepper Snails - spicy Nigerian snails cooked in pepper sauce',
  href: '#products'
},
{
  id: 'small-chops',
  name: 'Small Chops',
  description: 'A popular West African street snack made from a dough of flour',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11b2ac354-1770713401033.png",
  alt: 'Small Chops - assorted West African finger foods and snacks',
  href: '#products'
},
{
  id: 'meat-pie',
  name: 'Meat Pie',
  description: 'A savory pastry with a flaky crust, typically filled with a mixture of well-seasoned ground beef, diced potatoes, carrots, onions, and spices like curry powder, encased in a buttery dough',
  image: "/assets/images/Meat-Pie-1784765289508.jpg",
  alt: 'Nigerian Meat Pie - flaky pastry filled with seasoned ground beef and vegetables',
  href: '#products'
},
{
  id: 'chin-chin',
  name: 'Chin chin',
  description: 'A small, sweet crunchy, fried Nigerian snack that comes in a variety of shapes and sizes',
  image: "/assets/images/Chinchin-1784765399190.png",
  alt: 'Chin Chin - crunchy fried Nigerian snack in various shapes',
  href: '#products'
},
{
  id: 'gizdodo',
  name: 'GizDodo',
  description: 'The awesome fusion of Gizdodo! Chicken Gizzard, Fried Plantain, and Spicy Tomato Stew',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_14948c526-1767632596956.png",
  alt: 'GizDodo - Nigerian fusion dish with chicken gizzard and fried plantain',
  href: '#products'
}];


const hours: HoursEntry[] = [
{ day: 'Mon', dayFull: 'day', from: '9:00am', to: '10:00pm' },
{ day: 'Tue', dayFull: 'sday', from: '9:00am', to: '10:00pm' },
{ day: 'Wed', dayFull: 'nesday', from: '9:00am', to: '10:00pm' },
{ day: 'Thu', dayFull: 'rsday', from: '9:00am', to: '10:00pm' },
{ day: 'Fri', dayFull: 'day', from: '9:00am', to: '10:00pm' },
{ day: 'Sat', dayFull: 'urday', from: '9:00am', to: '6:00pm' }];


// ─── Arrow SVG ───────────────────────────────────────────────────────────────
const ArrowSvg = () =>
<svg fill="none" viewBox="0 0 10 17" xmlns="http://www.w3.org/2000/svg" className="inline-block align-middle ml-1" style={{ height: '0.8em', width: 'auto' }}>
    <path d="M1.25111 16.0843c-.320261 0-.640343-.1222-.88465-.3663-.488613-.4886-.488613-1.2807 0-1.7693l5.90679-5.90664L.36646 2.13545c-.488613-.48844-.488613-1.28069 0-1.769124.488434-.488435 1.28069-.488435 1.76912 0L8.92684 7.15759c.48844.48843.48844 1.28068 0 1.7693L2.13558 15.7181c-.24412.244-.56421.3662-.88447.3662Z" fill="#000" />
  </svg>;


// ─── Phone SVG ───────────────────────────────────────────────────────────────
const PhoneSvg = () =>
<svg viewBox="0 0 473.806 473.806" xmlns="http://www.w3.org/2000/svg" className="contact-icon" style={{ width: '1.25rem', height: '1.25rem', fill: '#595959', flexShrink: 0 }}>
    <path d="M374.456 293.506c-9.7-10.1-21.4-15.5-33.8-15.5-12.3 0-24.1 5.3-34.2 15.4l-31.6 31.5c-2.6-1.4-5.2-2.7-7.7-4-3.6-1.8-7-3.5-9.9-5.3-29.6-18.8-56.5-43.3-82.3-75-12.5-15.8-20.9-29.1-27-42.6 8.2-7.5 15.8-15.3 23.2-22.8 2.8-2.8 5.6-5.7 8.4-8.5 21-21 21-48.2 0-69.2l-27.3-27.3c-3.1-3.1-6.3-6.3-9.3-9.5-6-6.2-12.3-12.6-18.8-18.6-9.7-9.6-21.3-14.7-33.5-14.7s-24 5.1-34 14.7l-.2.2-34 34.3c-12.8 12.8-20.1 28.4-21.7 46.5-2.4 29.2 6.2 56.4 12.8 74.2 16.2 43.7 40.4 84.2 76.5 127.6 43.8 52.3 96.5 93.6 156.7 122.7 23 10.9 53.7 23.8 88 26 2.1.1 4.3.2 6.3.2 23.1 0 42.5-8.3 57.7-24.8.1-.2.3-.3.4-.5 5.2-6.3 11.2-12 17.5-18.1 4.3-4.1 8.7-8.4 13-12.9 9.9-10.3 15.1-22.3 15.1-34.6 0-12.4-5.3-24.3-15.4-34.3l-54.9-55.1zm35.8 105.3c-.1 0-.1.1 0 0-3.9 4.2-7.9 8-12.2 12.2-6.5 6.2-13.1 12.7-19.3 20-10.1 10.8-22 15.9-37.6 15.9-1.5 0-3.1 0-4.6-.1-29.7-1.9-57.3-13.5-78-23.4-56.6-27.4-106.3-66.3-147.6-115.6-34.1-41.1-56.9-79.1-72-119.9-9.3-24.9-12.7-44.3-11.2-62.6 1-11.7 5.5-21.4 13.8-29.7l34.1-34.1c4.9-4.6 10.1-7.1 15.2-7.1 6.3 0 11.4 3.8 14.6 7l.3.3c6.1 5.7 11.9 11.6 18 17.9 3.1 3.2 6.3 6.4 9.5 9.7l27.3 27.3c10.6 10.6 10.6 20.4 0 31-2.9 2.9-5.7 5.8-8.6 8.6-8.4 8.6-16.4 16.6-25.1 24.4-.2.2-.4.3-.5.5-8.6 8.6-7 17-5.2 22.7l.3.9c7.1 17.2 17.1 33.4 32.3 52.7l.1.1c27.6 34 56.7 60.5 88.8 80.8 4.1 2.6 8.3 4.7 12.3 6.7 3.6 1.8 7 3.5 9.9 5.3.4.2.8.5 1.2.7 3.4 1.7 6.6 2.5 9.9 2.5 8.3 0 13.5-5.2 15.2-6.9l34.2-34.2c3.4-3.4 8.8-7.5 15.1-7.5 6.2 0 11.3 3.9 14.4 7.3l.2.2 55.1 55.1c10.3 10.2 10.3 20.7.1 31.3z" />
  </svg>;


// ─── Email SVG ───────────────────────────────────────────────────────────────
const EmailSvg = () =>
<svg height="8px" version="1.1" viewBox="0 0 12 8" width="12px" xmlns="http://www.w3.org/2000/svg" style={{ fill: '#595959', flexShrink: 0 }}>
    <path d="M0 8h11.2V0H0v8zm5.6-2.673l1.095-.914 3.522 2.947H.983l3.522-2.947 1.095.914zm1.594-1.33l3.366-2.81v5.626L7.194 3.996zM5.6 4.492L.985.64h9.23L5.6 4.493zm-1.594-.497L.64 6.813V1.186l3.366 2.81z" fillRule="nonzero" stroke="none" strokeWidth="1" />
  </svg>;


// ─── Location SVG ────────────────────────────────────────────────────────────
const LocationSvg = () =>
<svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" style={{ width: '1.25rem', height: '1.25rem', fill: '#595959', flexShrink: 0 }}>
    <path d="M256 0C156.748 0 76 80.748 76 180c0 33.534 9.289 66.26 26.869 94.652l142.885 230.257A15 15 0 00258.499 512h.119a14.997 14.997 0 0012.75-7.292L410.611 272.22C427.221 244.428 436 212.539 436 180 436 80.748 355.252 0 256 0zm128.866 256.818L258.272 468.186l-129.905-209.34C113.734 235.214 105.8 207.95 105.8 180c0-82.71 67.49-150.2 150.2-150.2S406.1 97.29 406.1 180c0 27.121-7.411 53.688-21.234 76.818z" />
    <path d="M256 90c-49.626 0-90 40.374-90 90 0 49.309 39.717 90 90 90 50.903 0 90-41.233 90-90 0-49.626-40.374-90-90-90zm0 150.2c-33.257 0-60.2-27.033-60.2-60.2 0-33.084 27.116-60.2 60.2-60.2s60.1 27.116 60.1 60.2c0 32.683-26.316 60.2-60.1 60.2z" />
  </svg>;


// ─── Hamburger Menu ──────────────────────────────────────────────────────────
const HamburgerIcon = () =>
<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
    <path d="M3 12H21M3 6H21M3 18H21" stroke="white" strokeWidth="2" strokeLinecap="round" />
  </svg>;


const CloseIcon = () =>
<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5">
    <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" />
  </svg>;


// ─── Navbar ──────────────────────────────────────────────────────────────────
const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) {
      const navHeight = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - navHeight + 10;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-[9999] transition-all duration-200"
      style={{ backgroundColor: '#162415' }}>
      
      <div className="max-w-[1140px] mx-auto px-5 flex items-center justify-between" style={{ height: '80px' }}>
        {/* Logo */}
        <a
          href="#home"
          onClick={(e) => {e.preventDefault();scrollTo('home');}}
          className="no-underline"
          style={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: '1.65rem',
            fontWeight: 700,
            letterSpacing: '1.4px',
            textTransform: 'uppercase',
            color: '#fff'
          }}>
          
          ABtreat Service
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-6">
          {['home', 'about', 'products', 'contact'].map((item) =>
          <a
            key={item}
            href={`#${item}`}
            onClick={(e) => {e.preventDefault();scrollTo(item);}}
            className="nav-underline no-underline"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '0.875rem',
              fontWeight: 500,
              letterSpacing: '1px',
              textTransform: 'capitalize',
              color: '#fff',
              padding: '6px 0',
              display: 'inline-block',
              transition: 'color 0.15s ease-in-out'
            }}>
            
              {item.charAt(0).toUpperCase() + item.slice(1)}
            </a>
          )}
          <a
            href="#"
            className="no-underline"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '0.875rem',
              fontWeight: 500,
              letterSpacing: '1px',
              textTransform: 'capitalize',
              color: '#fff',
              padding: '6px 0',
              display: 'inline-block'
            }}>
            
            Menu
          </a>
          <a
            href="#contact"
            onClick={(e) => {e.preventDefault();scrollTo('contact');}}
            className="no-underline"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '0.875rem',
              fontWeight: 600,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              color: '#020402',
              border: '2px solid #020402',
              borderRadius: '2px',
              padding: '6px 20px',
              display: 'inline-flex',
              alignItems: 'center',
              transition: 'all 0.15s ease-in-out',
              backgroundColor: 'transparent'
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#32aa27';
              (e.currentTarget as HTMLAnchorElement).style.borderColor = '#32aa27';
              (e.currentTarget as HTMLAnchorElement).style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
              (e.currentTarget as HTMLAnchorElement).style.borderColor = '#020402';
              (e.currentTarget as HTMLAnchorElement).style.color = '#020402';
            }}>
            
            Contact
          </a>
        </nav>

        {/* Mobile hamburger */}
        <button
          className="lg:hidden p-1"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle navigation">
          
          {mobileOpen ? <CloseIcon /> : <HamburgerIcon />}
        </button>
      </div>

      {/* Mobile Nav */}
      {mobileOpen &&
      <div
        className="lg:hidden fixed inset-0 z-[10000] flex flex-col pt-20 px-8 pb-8"
        style={{ backgroundColor: '#162415' }}>
        
          <button
          className="absolute top-5 right-5"
          onClick={() => setMobileOpen(false)}
          aria-label="Close navigation">
          
            <CloseIcon />
          </button>
          {['home', 'about', 'products', 'contact'].map((item) =>
        <a
          key={item}
          href={`#${item}`}
          onClick={(e) => {e.preventDefault();scrollTo(item);}}
          className="no-underline py-4 border-b"
          style={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: '0.875rem',
            fontWeight: 500,
            letterSpacing: '1px',
            textTransform: 'capitalize',
            color: '#fff',
            borderColor: 'rgba(255,255,255,0.2)'
          }}>
          
              {item.charAt(0).toUpperCase() + item.slice(1)}
            </a>
        )}
          <a
          href="#"
          className="no-underline py-4 border-b"
          style={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: '0.875rem',
            fontWeight: 500,
            letterSpacing: '1px',
            textTransform: 'capitalize',
            color: '#fff',
            borderColor: 'rgba(255,255,255,0.2)'
          }}>
          
            Menu
          </a>
          <a
          href="#contact"
          onClick={(e) => {e.preventDefault();scrollTo('contact');}}
          className="no-underline mt-4"
          style={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: '0.875rem',
            fontWeight: 600,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            color: '#020402',
            border: '2px solid #020402',
            borderRadius: '2px',
            padding: '10px 20px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent'
          }}>
          
            Contact
          </a>
        </div>
      }
    </header>);

};

// ─── Hero Section ────────────────────────────────────────────────────────────
const HeroSection: React.FC = () => {
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const navHeight = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - navHeight + 10;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      className="hero-section"
      style={{
        backgroundImage: `linear-gradient(0deg, rgba(0,0,0,0.7), rgba(0,0,0,0.3)), url('https://cdn.b12.io/client_media/Ke26fUGL/1f676994-c7a4-11ef-8924-0242ac110002-png-hero_image.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '80vh'
      }}>
      
      <div className="max-w-[1140px] mx-auto px-5 w-full py-20">
        <div className="max-w-[75%]" style={{ maxWidth: '75%' }}>
          <h1
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: 'clamp(2rem, 5vw, 3.875rem)',
              fontWeight: 700,
              lineHeight: 1.25,
              color: '#fff',
              marginBottom: '10px'
            }}>
            
            Welcome to ABtreat Catering Services
          </h1>
          <div
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: 'clamp(1.25rem, 2vw, 1.5rem)',
              fontWeight: 400,
              lineHeight: 1.25,
              color: '#fff',
              marginBottom: '20px'
            }}>
            
            ABtreat bringing Flavors to Your Table Delicious Catering for Every Occasion.
          </div>
          <div className="flex flex-wrap gap-3 mt-5">
            <a
              href="#products"
              onClick={(e) => {e.preventDefault();scrollTo('products');}}
              className="no-underline"
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '1rem',
                fontWeight: 600,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: '#fff',
                backgroundColor: '#32aa27',
                border: '2px solid #32aa27',
                borderRadius: '2px',
                padding: '10px 24px',
                display: 'inline-flex',
                alignItems: 'center',
                transition: 'all 0.15s ease-in-out',
                minHeight: '50px'
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#26801d';
                (e.currentTarget as HTMLAnchorElement).style.borderColor = '#26801d';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#32aa27';
                (e.currentTarget as HTMLAnchorElement).style.borderColor = '#32aa27';
              }}>
              
              View products
            </a>
          </div>
        </div>
      </div>
    </section>);

};

// ─── About Section ───────────────────────────────────────────────────────────
const AboutSection: React.FC = () => {
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const navHeight = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - navHeight + 10;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <section
      id="about"
      style={{ backgroundColor: '#fff', padding: '80px 0' }}>
      
      <div className="max-w-[1140px] mx-auto px-5">
        <div className="flex flex-col md:flex-row items-center gap-10">
          {/* Text Content */}
          <div className="flex-1 min-w-0">
            <div
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '0.875rem',
                fontWeight: 600,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: '#32aa27',
                marginBottom: '10px'
              }}>
              
              Experience authentic flavors
            </div>
            <h2
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: 'clamp(1.375rem, 3vw, 2rem)',
                fontWeight: 600,
                lineHeight: 1.25,
                color: '#020402',
                marginBottom: '10px'
              }}>
              
              Savor the essence of Nigeria and Africa
            </h2>
            <div
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '1.125rem',
                fontWeight: 400,
                lineHeight: 1.5,
                color: '#595959',
                marginBottom: '20px'
              }}>
              
              <p style={{ margin: 0 }}>
                <strong style={{ color: '#595959' }}>
                  At ABtreat, located in Fall River, we deliver a rich culinary experience inspired by authentic Nigerian and African flavors. Our diverse menu features mouthwatering dishes like Jollof Rice, Efo Riro, and Pepper Soup, crafted with care to bring joy and satisfaction to every guest. Whether it&apos;s a wedding, birthday, or corporate event, our commitment to quality ensures each meal transforms your gatherings into unforgettable culinary experiences.
                </strong>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-5">
              <a
                href="#"
                className="no-underline"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '1rem',
                  fontWeight: 400,
                  color: '#595959',
                  lineHeight: 1.5,
                  textDecoration: 'underline'
                }}>
                
                Founded with a passion for bringing people together over great food, ABtreat Catering Services has been serving communities with love and excellence. Our mission is to make every meal memorable and every event a celebration of flavors.
              </a>
            </div>
            <div className="mt-5">
              <a
                href="#contact"
                onClick={(e) => {e.preventDefault();scrollTo('contact');}}
                className="no-underline"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '1rem',
                  fontWeight: 600,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  color: '#020402',
                  border: '2px solid #020402',
                  borderRadius: '2px',
                  padding: '10px 24px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  transition: 'all 0.15s ease-in-out',
                  minHeight: '50px',
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#32aa27';
                  (e.currentTarget as HTMLAnchorElement).style.borderColor = '#32aa27';
                  (e.currentTarget as HTMLAnchorElement).style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
                  (e.currentTarget as HTMLAnchorElement).style.borderColor = '#020402';
                  (e.currentTarget as HTMLAnchorElement).style.color = '#020402';
                }}>
                
                Get in touch
              </a>
            </div>
          </div>

          {/* Image */}
          <div className="flex-shrink-0 flex justify-center" style={{ flexBasis: '41.66667%' }}>
            <div className="about-img-wrapper">
              <Image
                src="https://cdn.b12.io/client_media/Ke26fUGL/65202e4a-c7a3-11ef-b6f6-0242ac110002-jpeg-hero_image.png"
                alt="African cuisine spread featuring various Nigerian dishes"
                fill
                style={{ objectFit: 'cover' }}
                sizes="(max-width: 768px) 100vw, 41vw" />
              
            </div>
          </div>
        </div>
      </div>
    </section>);

};

// ─── Products Section ────────────────────────────────────────────────────────
const ProductsSection: React.FC = () => {
  return (
    <section
      id="products"
      style={{ backgroundColor: '#fff', padding: '80px 0' }}>
      
      <div className="max-w-[1140px] mx-auto px-5">
        {/* Section Header */}
        <div className="mb-8">
          <div
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '0.875rem',
              fontWeight: 600,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              color: '#32aa27',
              marginBottom: '10px'
            }}>
            
            Taste the authentic
          </div>
          <h2
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: 'clamp(1.375rem, 3vw, 2rem)',
              fontWeight: 600,
              lineHeight: 1.25,
              color: '#020402',
              marginBottom: '10px'
            }}>
            
            Explore our rich nigerian and african flavors
          </h2>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {products.map((product) =>
          <div
            key={product.id}
            className="product-card cursor-pointer"
            style={{
              backgroundColor: '#fff',
              boxShadow: '0 5px 20px 0 rgba(0,0,0,0.15)',
              transition: 'transform 0.15s ease-in-out'
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLDivElement).style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLDivElement).style.transform = 'scale(1)';
            }}>
            
              {/* Product Image */}
              <div className="product-img-wrapper">
                <Image
                src={product.image}
                alt={product.alt}
                fill
                unoptimized={!product.image.startsWith('http')}
                style={{ objectFit: 'cover' }}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw" />
              
              </div>
              {/* Product Content */}
              <div style={{ padding: '16px' }}>
                <div
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  lineHeight: 1.25,
                  color: '#020402',
                  marginBottom: '8px',
                  transition: 'color 0.15s ease-in-out'
                }}>
                
                  {product.name}
                  <ArrowSvg />
                </div>
                <p
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '1.125rem',
                  fontWeight: 400,
                  lineHeight: 1.5,
                  color: '#595959',
                  margin: 0
                }}>
                
                  {product.description}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

// ─── Contact Section ─────────────────────────────────────────────────────────
const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    consent: false
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <section
      id="contact"
      style={{ backgroundColor: '#fff' }}>
      
      {/* Map */}
      <div className="map-placeholder" style={{ width: '100%', height: '400px', position: 'relative', overflow: 'hidden' }}>
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2975.0!2d-71.1481487!3d41.7192827!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDQzJzA5LjQiTiA3McKwMDgnNTMuMyJX!5e0!3m2!1sen!2sus!4v1234567890"
          width="100%"
          height="400"
          style={{ border: 0, display: 'block' }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="ABtreat Service Location Map" />
        
      </div>

      {/* Contact Content */}
      <div className="max-w-[1140px] mx-auto px-5 py-20">
        <div className="flex flex-col md:flex-row gap-10 items-start">
          {/* Form Column */}
          <div className="flex-1 min-w-0">
            <div
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '0.875rem',
                fontWeight: 600,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: '#32aa27',
                marginBottom: '10px'
              }}>
              
              Get in touch
            </div>
            <h2
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: 'clamp(1.375rem, 3vw, 2rem)',
                fontWeight: 600,
                lineHeight: 1.25,
                color: '#020402',
                marginBottom: '20px'
              }}>
              
              Experience the taste of africa today!
            </h2>

            {submitted ?
            <div
              style={{
                padding: '16px',
                backgroundColor: '#eef1ef',
                borderRadius: '2px',
                color: '#32aa27',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '1rem'
              }}>
              
                Thank you! Your message has been sent.
              </div> :

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label
                  htmlFor="contact-name"
                  className="form-label">
                  
                    Name <span style={{ color: '#ec6366' }}>*</span>
                  </label>
                  <input
                  id="contact-name"
                  type="text"
                  required
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                
                </div>

                {/* Email */}
                <div>
                  <label
                  htmlFor="contact-email"
                  className="form-label">
                  
                    Email address <span style={{ color: '#ec6366' }}>*</span>
                  </label>
                  <input
                  id="contact-email"
                  type="email"
                  required
                  className="form-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                
                </div>

                {/* Phone */}
                <div>
                  <label
                  htmlFor="contact-phone"
                  className="form-label">
                  
                    Phone number <span style={{ color: '#ec6366' }}>*</span>
                  </label>
                  <input
                  id="contact-phone"
                  type="tel"
                  required
                  className="form-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                
                </div>

                {/* Message */}
                <div>
                  <label
                  htmlFor="contact-message"
                  className="form-label">
                  
                    Message
                  </label>
                  <textarea
                  id="contact-message"
                  rows={4}
                  className="form-input"
                  style={{ resize: 'vertical', minHeight: '60px' }}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })} />
                
                </div>

                {/* Consent */}
                <div className="flex items-start gap-2">
                  <input
                  id="contact-consent"
                  type="checkbox"
                  required
                  checked={formData.consent}
                  onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                  style={{
                    width: '18px',
                    height: '18px',
                    marginTop: '2px',
                    flexShrink: 0,
                    border: '1px solid #6e6b6b',
                    backgroundColor: '#fff',
                    cursor: 'pointer'
                  }} />
                
                  <label
                  htmlFor="contact-consent"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '1rem',
                    fontWeight: 400,
                    color: '#020402',
                    cursor: 'pointer'
                  }}>
                  
                    I allow this website to store my submission so they can respond to my inquiry. <span style={{ color: '#ec6366' }}>*</span>
                  </label>
                </div>

                {/* Submit */}
                <button
                type="submit"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '1rem',
                  fontWeight: 600,
                  letterSpacing: '0px',
                  textTransform: 'uppercase',
                  color: '#fff',
                  backgroundColor: '#32aa27',
                  border: '2px solid #32aa27',
                  borderRadius: '2px',
                  padding: '10px 24px',
                  width: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease-in-out',
                  marginTop: '16px',
                  minHeight: '50px'
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#26801d';
                  (e.currentTarget as HTMLButtonElement).style.borderColor = '#26801d';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#32aa27';
                  (e.currentTarget as HTMLButtonElement).style.borderColor = '#32aa27';
                }}>
                
                  Submit
                </button>
              </form>
            }
          </div>

          {/* Contact Details Column */}
          <div style={{ flexBasis: '41.66667%', flexShrink: 0 }}>
            {/* Reach Out */}
            <div style={{ marginBottom: '24px' }}>
              <h3
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  lineHeight: 1.25,
                  color: '#020402',
                  marginBottom: '12px'
                }}>
                
                Reach out
              </h3>
              <div className="flex items-start gap-2 mt-3">
                <PhoneSvg />
                <a
                  href="tel:(774) 888-7930"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '1.125rem',
                    fontWeight: 400,
                    color: '#595959',
                    textDecoration: 'none',
                    lineHeight: 1
                  }}
                  onMouseEnter={(e) => {(e.currentTarget as HTMLAnchorElement).style.color = '#32aa27';}}
                  onMouseLeave={(e) => {(e.currentTarget as HTMLAnchorElement).style.color = '#595959';}}>
                  
                  (774) 888-7930
                </a>
              </div>
              <div className="flex items-start gap-2 mt-3">
                <EmailSvg />
                <a
                  href="mailto:Abifat646@yahoo.com"
                  className="email"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '1.125rem',
                    fontWeight: 400,
                    color: '#595959',
                    textDecoration: 'none',
                    lineHeight: 1,
                    textTransform: 'lowercase'
                  }}
                  onMouseEnter={(e) => {(e.currentTarget as HTMLAnchorElement).style.color = '#32aa27';}}
                  onMouseLeave={(e) => {(e.currentTarget as HTMLAnchorElement).style.color = '#595959';}}>
                  
                  Abifat646@yahoo.com
                </a>
              </div>
            </div>

            {/* Location */}
            <div style={{ marginBottom: '24px' }}>
              <h3
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  lineHeight: 1.25,
                  color: '#020402',
                  marginBottom: '12px'
                }}>
                
                Location
              </h3>
              <div className="flex items-start gap-2">
                <LocationSvg />
                <a
                  href="https://www.google.com/maps/place/Florence+Street+Fall+River+MA+02720+US"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '1.125rem',
                    fontWeight: 400,
                    color: '#595959',
                    textDecoration: 'none',
                    lineHeight: 1
                  }}
                  onMouseEnter={(e) => {(e.currentTarget as HTMLAnchorElement).style.color = '#32aa27';}}
                  onMouseLeave={(e) => {(e.currentTarget as HTMLAnchorElement).style.color = '#595959';}}>
                  
                  Florence Street<br />
                  Fall River, MA 02720 US
                </a>
              </div>
            </div>

            {/* Hours */}
            <div>
              <h3
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  lineHeight: 1.25,
                  color: '#020402',
                  marginBottom: '12px'
                }}>
                
                Hours
              </h3>
              <ol className="hours-table" aria-label="Working hours">
                {hours.map((h, i) =>
                <li key={i} className="hours-row">
                    <div className="hours-day">
                      {h.day}<span className="hidden md:inline">{h.dayFull}</span>
                    </div>
                    <div className="hours-from">{h.from}</div>
                    <div className="hours-dash">–</div>
                    <div className="hours-to">{h.to}</div>
                  </li>
                )}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

// ─── Footer ──────────────────────────────────────────────────────────────────
const Footer: React.FC = () => {
  return (
    <footer
      id="Footer"
      style={{ backgroundColor: '#162415', padding: '80px 0' }}>
      
      <div className="max-w-[1140px] mx-auto px-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <a
            href="/"
            className="no-underline"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '1.65rem',
              fontWeight: 700,
              letterSpacing: '1.4px',
              textTransform: 'uppercase',
              color: '#fff'
            }}>
            
            ABtreat
          </a>
        </div>
        {/* Web design attribution removed */}
      </div>
    </footer>);

};

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function HomePage() {
  return (
    <main style={{ paddingTop: '80px' }}>
      <Navbar />
      <HeroSection />
      <AboutSection />
      <ProductsSection />
      <ContactSection />
      <Footer />
    </main>);

}